import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatQuantidade } from './numberFormat.js';

const BRAND = {
  navy: [8, 24, 96],
  blue: [24, 48, 176],
  light: [244, 247, 255],
  gray: [75, 85, 99],
  dark: [8, 24, 96],
  line: [197, 208, 248],
  perda: [255, 241, 242],
};

const MARGIN_X = 10;
const FOTO_LADO = 16;
const FOTO_GAP = 1.5;
const FOTOS_POR_LINHA = 3;

function fmtDataProducao(ymd) {
  const s = String(ymd || '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function fmtDataHora(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDuracao(seg) {
  if (seg == null) return '—';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}min`;
}

function fmtParametros(parametros) {
  if (!Array.isArray(parametros) || parametros.length === 0) return '';
  return parametros
    .map((p) => {
      const nome = p.nome || p.label || p.chave || '';
      const valor = p.valor != null ? p.valor : p.value != null ? p.value : '';
      const un = p.unidade || p.unit || '';
      return `${nome}: ${valor}${un ? ' ' + un : ''}`;
    })
    .filter(Boolean)
    .join(' · ');
}

function fotosDaLinha(linha) {
  const lista = Array.isArray(linha.fotos_etapa) ? linha.fotos_etapa.filter((f) => f && f.url) : [];
  if (linha.foto_url && !lista.some((f) => f.url === linha.foto_url)) {
    lista.unshift({ nome: 'Foto', url: linha.foto_url });
  }
  return lista;
}

function textoEtapa(linha) {
  if (linha.tipo === 'perda') return 'Perda';
  if (linha.tipo === 'parametro') return linha.etapa_nome ? `Amostra · ${linha.etapa_nome}` : 'Amostra';
  return linha.etapa_nome || '—';
}

function textoParametros(linha, comFotos, fotosCarregadas) {
  const partes = [];
  const params = fmtParametros(linha.parametros);
  if (params) partes.push(params);
  if (linha.observacao) partes.push(linha.observacao);
  if (linha.equipamento_nome) partes.push(linha.equipamento_nome);
  if (comFotos) {
    const fotos = fotosDaLinha(linha);
    const falhas = fotos.filter((f) => !fotosCarregadas.get(f.url)).length;
    if (falhas > 0) partes.push(`${falhas} foto(s) não incluída(s)`);
  }
  return partes.join('\n') || '—';
}

function qtd(valor) {
  if (valor == null || valor === '') return '—';
  return formatQuantidade(valor, '—');
}

function alturaBlocoFotos(qtdFotos) {
  if (!qtdFotos) return 0;
  const linhas = Math.ceil(qtdFotos / FOTOS_POR_LINHA);
  return linhas * (FOTO_LADO + FOTO_GAP) + 1;
}

function blobParaDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function carregarFoto(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const tipo = String(blob.type || '').toLowerCase();
    const urlBaixa = String(url || '').toLowerCase();
    const png = tipo.includes('png') || urlBaixa.includes('.png');
    const jpeg = tipo.includes('jpeg') || tipo.includes('jpg') || urlBaixa.includes('.jpg') || urlBaixa.includes('.jpeg');
    const tipoGenerico = !tipo || tipo.includes('octet-stream');
    if (!png && !jpeg && !tipoGenerico) return null;
    const format = png ? 'PNG' : 'JPEG';
    const dataUrl = await blobParaDataUrl(blob);
    return { dataUrl, format, nome: '' };
  } catch {
    return null;
  }
}

async function carregarFotos(grupos) {
  const urls = new Set();
  for (const [, linhas] of grupos) {
    for (const linha of linhas) {
      fotosDaLinha(linha).forEach((f) => urls.add(f.url));
    }
  }
  const mapa = new Map();
  const lista = [...urls];
  const lote = 6;
  for (let i = 0; i < lista.length; i += lote) {
    const fatia = lista.slice(i, i + lote);
    const resultados = await Promise.all(fatia.map((url) => carregarFoto(url).then((foto) => [url, foto])));
    resultados.forEach(([url, foto]) => {
      if (foto) mapa.set(url, foto);
    });
  }
  return mapa;
}

function desenharFotos(doc, linha, cell, fotosCarregadas) {
  const fotos = fotosDaLinha(linha)
    .map((f) => ({ ...f, arquivo: fotosCarregadas.get(f.url) }))
    .filter((f) => f.arquivo);
  fotos.forEach((foto, i) => {
    const col = i % FOTOS_POR_LINHA;
    const row = Math.floor(i / FOTOS_POR_LINHA);
    const x = cell.x + 1.2 + col * (FOTO_LADO + FOTO_GAP);
    const y = cell.y + cell.height - alturaBlocoFotos(fotos.length) + row * (FOTO_LADO + FOTO_GAP);
    try {
      doc.addImage(foto.arquivo.dataUrl, foto.arquivo.format, x, y, FOTO_LADO, FOTO_LADO);
    } catch {
      /* foto ilegível para o PDF */
    }
  });
}

/**
 * Monta o PDF já paginado. Não baixa o arquivo.
 * Retorna a quantidade de páginas.
 */
export function montarRelatorioProducaoPdf(doc, { grupos, periodo, setorFiltro, totais, comFotos, fotosCarregadas }) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const fotos = fotosCarregadas || new Map();

  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, pageWidth, 16, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Relatório de Produção', MARGIN_X, 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(comFotos ? 'Com fotos dos parâmetros' : 'Sem fotos', MARGIN_X, 12);
  doc.text('QualiManager', pageWidth - MARGIN_X, 7, { align: 'right' });
  doc.text(periodo, pageWidth - MARGIN_X, 12, { align: 'right' });

  let y = 22;
  doc.setTextColor(...BRAND.dark);
  doc.setFontSize(8);
  const resumo = [
    `Registros: ${totais.registros}`,
    `Entrada: ${qtd(totais.entrada)}`,
    `Saída: ${qtd(totais.saida)}`,
    `Perda: ${qtd(totais.perda)}`,
    setorFiltro ? `Setor: ${setorFiltro}` : 'Setor: todos',
  ].join('    ');
  doc.text(resumo, MARGIN_X, y);
  y += 4;

  const head = [[
    'Data',
    'Lote',
    'Etapa',
    'Operador',
    'Início',
    'Fim',
    'Duração',
    'Entrada',
    'Saída',
    'Peso final',
    'Perda',
    'Parâmetros',
  ]];

  const columnStyles = {
    0: { cellWidth: 18 },
    1: { cellWidth: 22 },
    2: { cellWidth: 28 },
    3: { cellWidth: 20 },
    4: { cellWidth: 24 },
    5: { cellWidth: 24 },
    6: { cellWidth: 14 },
    7: { cellWidth: 16, halign: 'right' },
    8: { cellWidth: 16, halign: 'right' },
    9: { cellWidth: 16, halign: 'right' },
    10: { cellWidth: 14, halign: 'right' },
    11: { cellWidth: 'auto' },
  };

  grupos.forEach(([setorNome, linhas]) => {
    if (y > pageHeight - 28) {
      doc.addPage();
      y = 14;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...BRAND.navy);
    doc.text(String(setorNome || '—'), MARGIN_X, y);
    y += 2;

    autoTable(doc, {
      startY: y,
      head,
      body: linhas.map((linha) => [
        fmtDataProducao(linha.data_producao),
        linha.lote_fabricacao || '—',
        textoEtapa(linha),
        linha.operador_nome || '—',
        fmtDataHora(linha.tempo_inicio_ms),
        fmtDataHora(linha.tempo_fim_ms),
        fmtDuracao(linha.duracao_segundos),
        qtd(linha.quantidade_entrada),
        qtd(linha.quantidade_saida),
        linha.peso_final != null && linha.peso_final !== '' ? `${qtd(linha.peso_final)} kg` : '—',
        linha.perda ? qtd(linha.perda) : '',
        textoParametros(linha, comFotos, fotos),
      ]),
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 7,
        cellPadding: 1.2,
        textColor: BRAND.dark,
        lineColor: BRAND.line,
        lineWidth: 0.1,
        overflow: 'linebreak',
        valign: 'top',
      },
      headStyles: {
        fillColor: BRAND.navy,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7,
      },
      alternateRowStyles: { fillColor: BRAND.light },
      columnStyles,
      margin: { left: MARGIN_X, right: MARGIN_X, top: 12, bottom: 12 },
      rowPageBreak: 'auto',
      showHead: 'everyPage',
      didParseCell(data) {
        if (data.section !== 'body') return;
        const linha = linhas[data.row.index];
        if (!linha) return;
        if (linha.tipo === 'perda') data.cell.styles.fillColor = BRAND.perda;
        if (comFotos && data.column.index === 11) {
          const n = fotosDaLinha(linha).filter((f) => fotos.has(f.url)).length;
          const extra = alturaBlocoFotos(n);
          if (extra) {
            const texto = Array.isArray(data.cell.text) ? data.cell.text.join('\n') : '';
            const linhasTexto = String(texto || ' ').split('\n').reduce((acc, linha) => {
              return acc + Math.max(1, Math.ceil(linha.length / 42));
            }, 0);
            data.cell.styles.minCellHeight = linhasTexto * 3.4 + extra + 2;
          }
        }
      },
      didDrawCell(data) {
        if (!comFotos || data.section !== 'body' || data.column.index !== 11) return;
        const linha = linhas[data.row.index];
        if (!linha) return;
        desenharFotos(doc, linha, data.cell, fotos);
      },
    });

    y = (doc.lastAutoTable?.finalY || y) + 8;
  });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...BRAND.line);
    doc.line(MARGIN_X, pageHeight - 8, pageWidth - MARGIN_X, pageHeight - 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...BRAND.gray);
    doc.text('QualiManager — Relatório de Produção', MARGIN_X, pageHeight - 4);
    doc.text(`Página ${i} de ${total}`, pageWidth - MARGIN_X, pageHeight - 4, { align: 'right' });
  }

  return total;
}

export async function baixarRelatorioProducaoPdf({ grupos, periodo, setorFiltro, totais, comFotos, nomeArquivo }) {
  const fotosCarregadas = comFotos ? await carregarFotos(grupos) : new Map();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const paginas = montarRelatorioProducaoPdf(doc, {
    grupos,
    periodo,
    setorFiltro,
    totais,
    comFotos,
    fotosCarregadas,
  });
  doc.save(nomeArquivo);
  return paginas;
}
